import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import PipelineProgress from "./PipelineProgress";
import StageReview from "./StageReview";
import PipelineComplete from "./PipelineComplete";
import ArtifactViewer from "./ArtifactViewer";
import type { PipelineReviewStateResponse } from "../lib/types";
import type { Language } from "../i18n/translations";

interface PipelineReviewProps {
  projectId: string;
  language: Language;
}

export default function PipelineReview({
  projectId,
  language,
}: PipelineReviewProps) {
  const [reviewState, setReviewState] = useState<PipelineReviewStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [viewingApprovedStage, setViewingApprovedStage] = useState<string | null>(null);

  const fetchState = useCallback(async () => {
    try {
      const state = await api.getPipelineReviewState(projectId);
      setReviewState(state);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load pipeline state");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  // Auto-trigger generation when current stage needs it:
  // 1. "pending" with generation_count=0 → fresh stage, never generated
  // 2. "generating" with generation_count=0 and no artifact → stuck from a previous
  //    failed attempt (POST ran, set DB to "generating", but background task crashed)
  // Tracks which stage was triggered to allow re-triggering on stage transitions.
  const autoTriggerRef = useRef<string | null>(null);
  const generationInFlightRef = useRef(false);
  useEffect(() => {
    if (!reviewState) return;
    const currentStage = reviewState.current_stage;
    const currentInfo = reviewState.stages[currentStage];
    if (!currentInfo) return;

    const isPending = currentInfo.status === "pending" && currentInfo.generation_count === 0;
    const isStuckGenerating =
      currentInfo.status === "generating" &&
      currentInfo.generation_count === 0 &&
      !currentInfo.artifact_content;

    if (!isPending && !isStuckGenerating) return;
    if (autoTriggerRef.current === currentStage) return;
    if (generationInFlightRef.current) return;

    autoTriggerRef.current = currentStage;
    generationInFlightRef.current = true;

    (async () => {
      try {
        // Only set local "generating" if currently "pending" (avoid no-op state update)
        if (isPending) {
          setReviewState((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              stages: {
                ...prev.stages,
                [currentStage]: { ...prev.stages[currentStage], status: "generating" },
              },
            };
          });
        }
        await api.generatePipelineStage(projectId);
        // POST succeeded — backend status is now "generating".
        // Re-fetch so the polling useEffect picks up the authoritative state.
        await fetchState();
      } catch (err) {
        // Re-fetch to restore correct state on failure
        autoTriggerRef.current = null;
        await fetchState();
      } finally {
        generationInFlightRef.current = false;
      }
    })();
  }, [projectId, reviewState, fetchState]);

  // Manual retry after a "failed" generation — user clicks Retry button
  async function handleRetryGeneration() {
    if (!reviewState || generationInFlightRef.current) return;
    const currentStage = reviewState.current_stage;
    // Reset auto-trigger ref so it doesn't block
    autoTriggerRef.current = null;
    generationInFlightRef.current = true;
    // Optimistically show generating state
    setReviewState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stages: {
          ...prev.stages,
          [currentStage]: { ...prev.stages[currentStage], status: "generating", error: undefined },
        },
      };
    });
    setError(null);
    try {
      await api.generatePipelineStage(projectId);
      await fetchState();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Retry failed");
      await fetchState();
    } finally {
      generationInFlightRef.current = false;
    }
  }

  // Stale poll detection — show message after 3 identical polls (~15s)
  const [staleMessage, setStaleMessage] = useState<string | null>(null);
  const staleCountRef = useRef(0);
  const lastPollSnapshotRef = useRef<string>("");

  // Poll for generation completion when current stage is "generating"
  useEffect(() => {
    if (!reviewState) return;
    const currentInfo = reviewState.stages[reviewState.current_stage];
    if (!currentInfo || currentInfo.status !== "generating") {
      staleCountRef.current = 0;
      lastPollSnapshotRef.current = "";
      setStaleMessage(null);
      return;
    }

    const interval = setInterval(async () => {
      try {
        const state = await api.getPipelineReviewState(projectId);
        setReviewState(state);

        // Stale detection: compare current_stage + statuses
        const snapshot = JSON.stringify({
          stage: state.current_stage,
          statuses: Object.fromEntries(
            Object.entries(state.stages).map(([k, v]) => [k, v.status]),
          ),
        });
        if (snapshot === lastPollSnapshotRef.current) {
          staleCountRef.current += 1;
          if (staleCountRef.current >= 3) {
            setStaleMessage(
              "Still working \u2014 generation typically takes 2\u20133 minutes per step",
            );
          }
        } else {
          staleCountRef.current = 0;
          setStaleMessage(null);
          lastPollSnapshotRef.current = snapshot;
        }

        const updatedInfo = state.stages[state.current_stage];
        if (updatedInfo && updatedInfo.status !== "generating") {
          clearInterval(interval);
          staleCountRef.current = 0;
          setStaleMessage(null);
        }
      } catch { /* keep polling */ }
    }, 5000);

    return () => clearInterval(interval);
  }, [projectId, reviewState?.current_stage, reviewState?.stages]);

  async function handleSendMessage(message: string) {
    setSendingMessage(true);
    try {
      const res = await api.sendPipelineReviewMessage(projectId, message);
      // Update messages in local state
      setReviewState((prev) => {
        if (!prev) return prev;
        const stage = prev.current_stage;
        const stageInfo = prev.stages[stage];
        if (!stageInfo) return prev;
        return {
          ...prev,
          stages: {
            ...prev.stages,
            [stage]: {
              ...stageInfo,
              // If the AI triggered regeneration, set status to "generating"
              status: res.regenerating ? "generating" : stageInfo.status,
              review_messages: [
                ...stageInfo.review_messages,
                { role: "user" as const, content: message },
                { role: "assistant" as const, content: res.message },
              ],
            },
          },
        };
      });
    } catch (err) {
      // Show error as system message
      setReviewState((prev) => {
        if (!prev) return prev;
        const stage = prev.current_stage;
        const stageInfo = prev.stages[stage];
        if (!stageInfo) return prev;
        return {
          ...prev,
          stages: {
            ...prev.stages,
            [stage]: {
              ...stageInfo,
              review_messages: [
                ...stageInfo.review_messages,
                { role: "user" as const, content: message },
                { role: "assistant" as const, content: `Error: ${err instanceof Error ? err.message : "Failed to send message"}` },
              ],
            },
          },
        };
      });
    } finally {
      setSendingMessage(false);
    }
  }

  async function handleApprove() {
    try {
      await api.approvePipelineStage(projectId);
      // Re-fetch to get new state (next stage may be generating)
      await fetchState();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve stage");
    }
  }

  function handleStageClick(stage: string) {
    if (!reviewState) return;
    const info = reviewState.stages[stage];
    if (info?.status === "approved" && info.artifact_content) {
      setViewingApprovedStage(stage);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-400">Loading pipeline review...</span>
        </div>
      </div>
    );
  }

  if (error && !reviewState) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
        <p className="text-sm text-red-400 mb-2">{error}</p>
        <button
          onClick={() => { setLoading(true); setError(null); fetchState(); }}
          className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-lg px-3 py-1.5 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!reviewState) return null;

  const isComplete = reviewState.current_stage === "complete";
  const currentStage = reviewState.current_stage;
  const currentStageInfo = reviewState.stages[currentStage];
  const isGenerating = currentStageInfo?.status === "generating";
  const isFailed = currentStageInfo?.status === "failed";
  const stageKeys = Object.keys(reviewState.stages);
  const isLastStage = currentStage === stageKeys[stageKeys.length - 1];

  if (isComplete) {
    return (
      <PipelineComplete
        projectId={projectId}
        reviewState={reviewState}
        language={language}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* Progress indicator */}
      <PipelineProgress
        stages={reviewState.stages}
        currentStage={currentStage}
        onStageClick={handleStageClick}
      />

      {/* Error banner */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-3 flex items-center justify-between">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-300 text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Generation failed — show error + retry */}
      {isFailed && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
          <p className="text-sm font-medium text-red-400 mb-1">
            Generation failed for {currentStage.replace(/_/g, " ")}
          </p>
          {currentStageInfo?.error && (
            <p className="text-xs text-red-400/70 mb-3 font-mono">{currentStageInfo.error}</p>
          )}
          <button
            onClick={handleRetryGeneration}
            className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-lg px-4 py-2 transition-colors"
          >
            Retry Generation
          </button>
        </div>
      )}

      {/* Stage review */}
      {currentStageInfo && !isFailed && (
        <StageReview
          stage={currentStage}
          stageInfo={currentStageInfo}
          isGenerating={isGenerating}
          isLast={isLastStage}
          onSendMessage={handleSendMessage}
          onApprove={handleApprove}
          onDownloadPdf={() => api.downloadPipelineStage(projectId, currentStage)}
          sendingMessage={sendingMessage}
          staleMessage={staleMessage}
        />
      )}

      {/* Viewing an approved stage's artifact */}
      {viewingApprovedStage && reviewState.stages[viewingApprovedStage]?.artifact_content && (
        <ArtifactViewer
          stage={viewingApprovedStage}
          content={reviewState.stages[viewingApprovedStage].artifact_content!}
          onClose={() => setViewingApprovedStage(null)}
          onDownload={() => api.downloadPipelineStage(projectId, viewingApprovedStage)}
        />
      )}
    </div>
  );
}
